import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, HeadingLevel } from 'docx';
import { jsPDF } from 'jspdf';

// Helper function to format date
const formatDate = () => {
  const date = new Date();
  return date.toISOString().split('T')[0];
};

// Generate Word document
const generateWordDoc = async (title, questions, answers) => {
  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
        }),
        new Paragraph({
          text: `Generated on ${formatDate()}`,
          spacing: { before: 400 },
        }),
        ...questions.map((question) => [
          new Paragraph({
            text: question.question_text,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400 },
          }),
          new Paragraph({
            text: `Paragraph: ${question.paragraph}${question.related_ar ? ` | Related AR: ${question.related_ar}` : ''} | Data Type: ${question.data_type}`,
            spacing: { before: 200 },
          }),
          new Paragraph({
            text: answers[question.id] || 'No answer provided',
            spacing: { before: 200 },
          }),
        ]).flat(),
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${formatDate()}.docx`);
};

// Generate PDF document
const generatePDF = (title, questions, answers) => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    const lineHeight = 8;  // Increased line height
    let yPos = margin;
    let pageNumber = 1;

    // Set default font
    doc.setFont('helvetica');

    // Helper function to add text and handle page breaks
    const addText = (text, size, isBold = false, color = '#000000') => {
      const currentY = yPos; // Store current Y position
      doc.setFontSize(size);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(color);
      
      const lines = doc.splitTextToSize(text || '', contentWidth);
      const totalHeight = lines.length * lineHeight;
      
      // Check if we need a new page
      if (currentY + totalHeight > pageHeight - 30) {
        doc.addPage();
        pageNumber++;
        yPos = margin;
        // Add page number
        doc.setFontSize(10);
        doc.setTextColor('#666666');
        doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
      }

      lines.forEach(line => {
        doc.text(line, margin, yPos);
        yPos += lineHeight;
      });

      return totalHeight;
    };

    // Add title
    doc.setDrawColor(31, 58, 147);
    doc.setLineWidth(0.5);
    doc.line(margin, 15, pageWidth - margin, 15);
    yPos = 30;
    addText(title, 16, true, '#1F3A93');
    
    // Add date
    yPos += 5;
    addText(`Generated on ${formatDate()}`, 10, false, '#666666');
    yPos += 15;

    // Add questions and answers
    questions.forEach((question, index) => {
      // Add question number and text
      addText(`${index + 1}. ${question.question_text}`, 12, true);
      yPos += 5;
      
      // Add metadata
      const metadata = `Paragraph: ${question.paragraph}${question.related_ar ? ` | Related AR: ${question.related_ar}` : ''} | Data Type: ${question.data_type}`;
      addText(metadata, 9, false, '#666666');
      yPos += 5;
      
      // Add answer
      const answer = answers[question.id] || 'No answer provided';
      addText(answer, 11);
      
      // Add separator line and spacing
      yPos += 10;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 15;
    });

    // Add page number to first page
    doc.setFontSize(10);
    doc.setTextColor('#666666');
    doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

    // Save the PDF
    const filename = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${formatDate()}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error('PDF generation failed:', error);
    throw new Error('Failed to generate PDF report. Please try again.');
  }
};

// Generate CSV file
const generateCSV = (title, questions, answers) => {
  const rows = [
    ['Question', 'Paragraph', 'Related AR', 'Data Type', 'Answer'], // Header row
    ...questions.map(question => [
      question.question_text,
      question.paragraph,
      question.related_ar || '',
      question.data_type,
      answers[question.id] || ''
    ])
  ];

  const csvContent = rows
    .map(row => 
      row.map(cell => 
        `"${(cell || '').toString().replace(/"/g, '""')}"`
      ).join(',')
    )
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${formatDate()}.csv`);
};

// Helper function to sanitize text for XML
const sanitizeXML = (text) => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

// Generate iXBRL document
const generateIXBRL = (title, questions, answers) => {
  const reportDate = formatDate();
  const companyData = {
    name: 'Your Company Name', // TODO: Get from project settings
    leiCode: '00000000000000000000', // TODO: Get from project settings
    reportingPeriodEnd: reportDate,
    reportingCurrency: 'EUR'
  };

  // Create the iXBRL document structure
  const ixbrlContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" 
      xmlns:ix="http://www.xbrl.org/2013/inlineXBRL" 
      xmlns:ixt="http://www.xbrl.org/inlineXBRL/transformation/2015-02-26"
      xmlns:xbrli="http://www.xbrl.org/2003/instance"
      xmlns:esrs="http://www.esrs.eu/taxonomy/2024"
      xmlns:esrs-dr="http://www.esrs.eu/taxonomy/2024/dr"
      xmlns:esrs-bh="http://www.esrs.eu/taxonomy/2024/business-header"
      xmlns:link="http://www.xbrl.org/2003/linkbase"
      xmlns:xlink="http://www.w3.org/1999/xlink">
<head>
  <title>
    ${sanitizeXML(title)} - ESRS Report
  </title>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <style type="text/css">
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
    }
    .report-header {
      background: #1F3A93;
      color: #fff;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
    }
    .report-header h1 {
      margin: 0;
      font-size: 28px;
    }
    .company-info {
      margin: 20px 0;
      padding: 15px;
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 6px;
    }
    .question {
      margin: 30px 0;
      padding: 20px;
      background: #fff;
      border: 1px solid #e9ecef;
      border-left: 4px solid #1F3A93;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .question h3 {
      color: #2c3e50;
      margin-top: 0;
    }
    .metadata {
      font-size: 0.9em;
      color: #6c757d;
      padding: 10px 0;
      border-bottom: 1px solid #e9ecef;
    }
    .answer {
      margin: 15px 0;
      padding: 15px;
      background: #f0f0f0;
      border-radius: 6px;
    }
    .answer:hover {
      background: #e9ecef;
    }
    .xbrl-tag {
      color: #0066cc;
      font-size: 0.8em;
      font-family: monospace;
      margin-top: 5px;
    }
  </style>
</head>
<body>
  <div class="report-header">
    <h1>
      <ix:nonNumeric name="esrs-bh:ReportTitle" contextRef="ctx-metadata">
        ${sanitizeXML(title)}
      </ix:nonNumeric>
    </h1>
    
    <div class="company-info">
      <p>
        <strong>Company Name: </strong>
        <ix:nonNumeric name="esrs-bh:CompanyName" contextRef="ctx-metadata">
          ${sanitizeXML(companyData.name)}
        </ix:nonNumeric>
      </p>
      <p>
        <strong>LEI: </strong>
        <ix:nonNumeric name="esrs-bh:LegalEntityIdentifier" contextRef="ctx-metadata">
          ${companyData.leiCode}
        </ix:nonNumeric>
      </p>
      <p>
        <strong>Reporting Date: </strong>
        <ix:nonNumeric name="esrs-bh:ReportingPeriodEndDate" contextRef="ctx-metadata">
          ${companyData.reportingPeriodEnd}
        </ix:nonNumeric>
      </p>
    </div>
  </div>

  <div class="report-content">
    ${questions.map((question, index) => `
      <div class="question">
        <h3>${sanitizeXML(question.question_text)}</h3>
        <div class="metadata">
          <span>Paragraph: ${sanitizeXML(question.paragraph)}</span>
          ${question.related_ar ? ` | Related AR: ${sanitizeXML(question.related_ar)}` : ''}
          | Data Type: ${sanitizeXML(question.data_type)}
        </div>
        <div class="answer">
          <ix:nonNumeric 
            name="esrs-dr:${sanitizeXML(question.code || `DR${index + 1}`)}"
            contextRef="ctx-${index}"
            format="ixt:string">
            ${sanitizeXML(answers[question.id] || 'No answer provided')}
          </ix:nonNumeric>
          <div class="xbrl-tag">
            XBRL Tag: esrs-dr:${sanitizeXML(question.code || `DR${index + 1}`)}
          </div>
        </div>
      </div>
    `).join('')}
  </div>
    
  <ix:hidden>
    <xbrli:context id="ctx-metadata">
      <xbrli:entity>
        <xbrli:identifier scheme="http://standards.iso.org/iso/17442">${companyData.leiCode}</xbrli:identifier>
      </xbrli:entity>
      <xbrli:period>
        <xbrli:instant>${companyData.reportingPeriodEnd}</xbrli:instant>
      </xbrli:period>
    </xbrli:context>

    ${questions.map((question, index) => `
      <xbrli:context id="ctx-${index}">
        <xbrli:entity>
          <xbrli:identifier scheme="http://standards.iso.org/iso/17442">${companyData.leiCode}</xbrli:identifier>
        </xbrli:entity>
        <xbrli:period>
          <xbrli:instant>${companyData.reportingPeriodEnd}</xbrli:instant>
        </xbrli:period>
      </xbrli:context>
    `).join('')}

    <link:schemaRef 
      xlink:type="simple" 
      xlink:href="https://www.esrs.eu/taxonomy/2024/esrs-full-2024.xsd"
      xlink:arcrole="http://www.w3.org/1999/xlink/properties/linkbase"/>
  </ix:hidden>
</body>
</html>`;

  // Create and download the file
  const blob = new Blob([ixbrlContent], { type: 'application/xhtml+xml' });
  saveAs(blob, `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${formatDate()}_ixbrl.xhtml`);
};

export async function generateReport(format, data) {
  switch (format) {
    case 'canva':
      alert('Canva export feature coming soon!');
      return;
    case 'xbrl':
      return generateIXBRL(data.title, data.questions, data.answers);
    case 'word':
      return await generateWordDoc(data.title, data.questions, data.answers);
    case 'pdf':
      return generatePDF(data.title, data.questions, data.answers);
    case 'csv':
      return generateCSV(data.title, data.questions, data.answers);
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
} 